// wiki-client: fetch wrapper for the Worker `/wiki/*` routes.
// Style mirrors src/shared/sync-client.ts (VITE_WORKER_URL + raw fetch).
//
// ReviewPaper / ReviewQuestion / DictationItem are owned by
// src/learn/review/types.ts (canonical, spec §5). Re-exported here so callers of
// wiki-client keep a single import surface without duplicating the schema.

import type { Subject } from './types';
import type { ReviewPaper } from '../learn/review/types';
export type { ReviewPaper, ReviewQuestion, DictationItem } from '../learn/review/types';

const WORKER_URL =
  (import.meta as any).env?.VITE_WORKER_URL ?? 'http://localhost:8787';

// ── Wiki data model (mirrors worker/src/wiki/types.ts spec §3.2) ──────────────

export interface WikiIndexEntry {
  pageId: string;
  title: string;
  summary: string;
  kp: string;
  subject: Subject;
}

export interface WikiPage {
  pageId: string;
  kind: 'concept' | 'error-pattern' | 'vocab';
  subject: Subject;
  kp: string;
  title: string;
  body: string;
  sourceQuestionIds: string[];
  masteryHint: 'weak' | 'improving' | 'mastered';
  lastTouchedAt: string;
}

export interface WikiLogEntry {
  at: string;
  action: 'ingest' | 'lint' | 'review';
  note: string;
  touchedPageIds: string[];
}

export interface StudentWiki {
  familyCode: string;
  schemaVersion: 1;
  updatedAt: string;
  index: WikiIndexEntry[];
  pages: Record<string, WikiPage>;
  log: WikiLogEntry[];
}

// ── Ingest / review request shapes ────────────────────────────────────────────

export interface IngestSource {
  kind: 'mistake' | 'extracted' | 'vocab';
  subject: Subject;
  kp: string;
  questionId?: string;
  prompt: string;
  correctAnswer?: string;
}

export interface ReviewOptions {
  subjects: Subject[];
  count: number;
  mode?: string;
  includeDictation?: boolean;
}

// ── API ───────────────────────────────────────────────────────────────────────

/**
 * Fetch the full StudentWiki for a family code.
 * Returns null when the wiki has not been created yet (404).
 * Throws on other non-2xx statuses.
 */
export async function getWiki(familyCode: string): Promise<StudentWiki | null> {
  const res = await fetch(`${WORKER_URL}/wiki/${familyCode}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`getWiki failed: ${res.status}`);
  return (await res.json()) as StudentWiki;
}

/**
 * Ingest new learning sources into the wiki (Karpathy incremental ingest).
 * Returns the page ids the worker touched.
 */
export async function ingestToWiki(
  familyCode: string,
  sources: IngestSource[],
): Promise<{ ok: boolean; touchedPageIds: string[] }> {
  const res = await fetch(`${WORKER_URL}/wiki/${familyCode}/ingest`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ sources }),
  });
  if (!res.ok) throw new Error(`ingestToWiki failed: ${res.status}`);
  return (await res.json()) as { ok: boolean; touchedPageIds: string[] };
}

/**
 * Generate a double-sided review paper from the wiki's weak points.
 */
export async function generateReviewPaper(
  familyCode: string,
  opts: ReviewOptions,
): Promise<ReviewPaper> {
  const res = await fetch(`${WORKER_URL}/wiki/${familyCode}/review`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(opts),
  });
  if (!res.ok) throw new Error(`generateReviewPaper failed: ${res.status}`);
  return (await res.json()) as ReviewPaper;
}
